import { motion } from "framer-motion";
import { Droplets, Heart, Shield } from "lucide-react";
import { BackButton } from "./ui/BackButton";
import { ramadanPeriods, type RamadanPeriod } from "@/data/dailyDuas";

interface PeriodOverviewProps {
  onSelectPeriod: (startDay: number) => void;
  onBackToCover: () => void;
  completedDays: number[];
}

const periodIcons = {
  1: Droplets,
  2: Heart,
  3: Shield,
};

const periodColors = {
  1: {
    bg: "from-emerald-50 to-teal-50",
    border: "border-emerald-200",
    text: "text-emerald-700",
    accent: "bg-emerald-500",
  },
  2: {
    bg: "from-amber-50 to-orange-50",
    border: "border-amber-200",
    text: "text-amber-700",
    accent: "bg-amber-500",
  },
  3: {
    bg: "from-purple-50 to-indigo-50",
    border: "border-purple-200",
    text: "text-purple-700",
    accent: "bg-purple-500",
  },
};

export const PeriodOverview = ({ 
  onSelectPeriod, 
  onBackToCover,
  completedDays 
}: PeriodOverviewProps) => {
  
  const getCompletedCount = (period: RamadanPeriod) => {
    const startDay = (period.id - 1) * 10 + 1;
    const endDay = period.id * 10;
    return completedDays.filter(d => d >= startDay && d <= endDay).length;
  };

  return (
    <div className="min-h-screen bg-paper">
      {/* Header */}
      <header className="border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex justify-between items-center">
          <BackButton onClick={onBackToCover} label="Dashboard" srLabel="Return to dashboard" />
          <span className="text-xs uppercase tracking-[0.2em] text-gold">
            The Three Periods
          </span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-12"
        >
          {/* Title */}
          <header className="text-center space-y-4">
            <p className="text-xs uppercase tracking-[0.3em] text-gold">
              Ramadan's Journey
            </p>
            <h1 className="font-display text-3xl md:text-4xl text-ink">
              30 Days, 3 Periods
            </h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              Ramadan is divided into three blessed periods, each with its own spiritual focus and blessings.
            </p>
            <div className="divider-elegant mt-6" />
          </header>

          {/* Period Cards */}
          <div className="space-y-6">
            {ramadanPeriods.map((period, index) => {
              const Icon = periodIcons[period.id];
              const colors = periodColors[period.id];
              const completedCount = getCompletedCount(period);
              const startDay = (period.id - 1) * 10 + 1;

              return (
                <motion.button
                  key={period.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => onSelectPeriod(startDay)}
                  className={`w-full text-left bg-gradient-to-br ${colors.bg} ${colors.border} border rounded-sm p-6 hover:shadow-md transition-all group`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`${colors.accent} p-3 rounded-full text-white`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-xs uppercase tracking-wider ${colors.text}`}>
                            {period.days}
                          </p>
                          <h2 className="font-display text-xl text-ink">
                            {period.name}
                          </h2>
                        </div>
                        <div className="text-right">
                          <p className="font-arabic text-lg text-muted-foreground" dir="rtl">
                            {period.arabicName}
                          </p>
                          <p className={`text-xs ${colors.text}`}>
                            {completedCount}/10 days
                          </p>
                        </div>
                      </div>
                      <p className={`text-sm ${colors.text} font-medium`}>
                        {period.theme}
                      </p>
                      <p className="text-sm text-foreground/70 leading-relaxed">
                        {period.description}
                      </p>
                      
                      {/* Progress bar */}
                      <div className="mt-4 h-1.5 bg-background rounded-full overflow-hidden">
                        <motion.div
                          className={colors.accent}
                          initial={{ width: 0 }}
                          animate={{ width: `${(completedCount / 10) * 100}%` }}
                          transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 text-right">
                    <span className={`text-xs ${colors.text} group-hover:translate-x-1 inline-block transition-transform`}>
                      Begin this period →
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="bg-cream rounded-sm p-6 text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              Each period contains 10 days of authentic Prophetic and Quranic duas, 
              specially curated to match the spiritual theme of that period.
            </p>
            <p className="text-xs text-gold italic">
              "Whoever fasts during Ramadan out of sincere faith and hoping to attain Allah's rewards, 
              all his past sins will be forgiven." — Bukhari
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
};
